import * as React from 'react';
import { Uppy } from '@uppy/core';
import { Dashboard } from '@uppy/react';
import * as XHRUpload from '@uppy/xhr-upload';
import { fileToBase64 } from '../utils/helpers';
// @ts-ignore
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import styled from 'styled-components';
import { space } from 'styled-system';
import { StyledSystemProps } from '../theme/StyledSystemProps';
import { Button } from '.';
import { ImageObject } from '../utils/types';

require('@uppy/core/dist/style.css');
require('@uppy/dashboard/dist/style.css');

interface Props {
  onComplete: (uploadedImage: ImageObject) => void;
  onFileAdded?: () => void;
  handleUploadSuccess: (data: UppyResponse[]) => void;
  allowedFileTypes: string[];
  dragDrop?: boolean;
  autoProceed?: boolean;
  maxNumberOfFiles?: number;
  initialImage?: ImageObject;
}

export type UppyResponse = {
  response: {
    body: {
      scaled: string;
      thumbnail: string;
      original: string;
    };
  };
};

const ImageCropModal = styled.div<StyledSystemProps>`
  ${space};
  display: flex;
  overflow: hidden;
  flex-flow: column;
  position: relative;
  border-radius: 4px;
  box-sizing: border-box;
  position: relative;
  margin-left: 2.5%;
  max-width: 500px;
`;

const ButtonCrop = styled.div<StyledSystemProps>`
  width: 100%;
  max-width: 200px;
  margin: 0 auto;
  text-align: center;
`;

const ButtonRow = styled.div`
  margin-bottom: 8px;
  display: flex;
  justify-content: center;
  gap: 8px;
`;

type uploaderCropState = {
  cropImage: boolean;
  creatingCrop: boolean;
  crop: ReactCrop.Crop;
  image: string;
  imageRef: any;
  uppyRef: any;
  fileType: string;
};

class UploaderCrop extends React.Component<Props, uploaderCropState> {
  static defaultProps = {
    autoProceed: true,
  };

  uppy: Uppy;

  constructor(props: Props) {
    super(props);
    const { allowedFileTypes, maxNumberOfFiles } = this.props;
    // const { allowedFileTypes, onFileAdded, maxNumberOfFiles } = this.props;
    const cropInit: ReactCrop.Crop = {
      aspect: 16 / 10,
      x: 12.5,
      y: 5,
      width: 75,
      height: 75,
      unit: '%',
    };
    this.state = {
      cropImage: false,
      crop: cropInit,
      image: '',
      imageRef: null,
      creatingCrop: false,
      uppyRef: null,
      fileType: '',
    };
    this.uppy = new Uppy({
      allowMultipleUploads: false,
      autoProceed: false,
      onBeforeUpload: (files) => {
        const { cropImage, image, fileType } = this.state;
        const awaitingCrop = cropImage && image !== '' && fileType.startsWith('image/');
        if (awaitingCrop) {
          this.uppy.info(
            'Use "CROP IMAGE" to apply your crop before uploading the image.',
            'warning',
            6000
          );
          return false;
        }
        return files;
      },
      restrictions: {
        maxFileSize: 1500000,
        maxNumberOfFiles: maxNumberOfFiles || 1,
        minNumberOfFiles: 1,
        allowedFileTypes,
      },
    });

    this.uppy.on('file-added', async (file) => {
      const base64 = await fileToBase64(file.data);
      this.setState({
        cropImage: true,
        image: base64,
        uppyRef: file,
        fileType: file.type,
      });
    });

    this.uppy.on('complete', (result: any) => {
      const { onComplete } = this.props;
      if (result && result.successful && result.successful.length > 0) {
        const urls = result.successful[0].response.body;
        onComplete(urls);
      }
    });
    this.cropImageComplete = this.cropImageComplete.bind(this);
    this.cropImageHandler = this.cropImageHandler.bind(this);
    this.setImageCrop = this.setImageCrop.bind(this);
    this.onImageLoad = this.onImageLoad.bind(this);
    this.setFixedAspectRatio = this.setFixedAspectRatio.bind(this);
    this.clearAspectRatio = this.clearAspectRatio.bind(this);
  }

  setFixedAspectRatio() {
    this.setState(({ crop }) => ({
      crop: {
        ...crop,
        aspect: 16 / 10,
      },
    }));
  }

  clearAspectRatio() {
    this.setState(({ crop }) => {
      const { aspect, ...rest } = crop;
      return {
        crop: rest as ReactCrop.Crop,
      };
    });
  }

  onImageLoad(img: any) {
    this.setState({
      imageRef: img,
    });
  }

  setImageCrop(crop: ReactCrop.Crop) {
    this.setState({
      crop,
    });
  }

  cropImageHandler() {
    this.setState({ creatingCrop: true });
    const img = this.state.imageRef || new Image();
    const newCrop = this.state.crop;

    const canvas = document.createElement('canvas');
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    canvas.width = newCrop.width || 0;
    canvas.height = newCrop.height || 0;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      if (img) {
        ctx.drawImage(
          img,
          (newCrop.x || 0) * scaleX,
          (newCrop.y || 0) * scaleY,
          (newCrop.width || 0) * scaleX,
          (newCrop.height || 0) * scaleY,
          0,
          0,
          newCrop.width || 0,
          newCrop.height || 0
        );
      }
    }
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        this.setState({
          cropImage: false,
          image: '',
          creatingCrop: false,
        });
        this.cropImageComplete(blob);
      }, this.state.fileType || 'image/png');
    });
  }

  async cropImageComplete(file: any) {
    const base64 = await fileToBase64(file);
    const { uppyRef } = this.state;
    const xhrUploadOptions = {
      endpoint: `https://c734jixdhg.execute-api.eu-west-1.amazonaws.com/prod/`,
      headers: { 'content-type': uppyRef.type },
      formData: false,
    };
    this.uppy.setFileState(uppyRef.id, {
      data: base64.split(',').pop(),
      preview: window.URL.createObjectURL(file),
    });

    const xhrUploadPlugin: any = this.uppy.getPlugin('XHRUpload');
    if (!xhrUploadPlugin) {
      this.uppy.use(XHRUpload as any, xhrUploadOptions);
    } else if (xhrUploadPlugin.setOptions) {
      xhrUploadPlugin.setOptions(xhrUploadOptions);
    }

    if (this.props.onFileAdded) {
      this.props.onFileAdded();
    }

  }

  render() {
    const { fileType } = this.state;

    return (
      <>
        <Dashboard uppy={this.uppy} height={400} width={400} />
        {this.state.cropImage && this.state.image !== '' && (
          <ImageCropModal>
            {fileType.startsWith('image/') && (
              // Renders the crop buttons only if the fileType starts with 'image/'
              <ButtonCrop>
                <Button
                  mb={1}
                  type="submit"
                  text="CROP IMAGE"
                  onClick={this.cropImageHandler}
                />
                <ButtonRow>
                  <Button
                    mb={0}
                    width="auto"
                    minWidth="0"
                    type="button"
                    text="16:10"
                    onClick={this.setFixedAspectRatio}
                  />
                  <Button
                    mb={0}
                    width="auto"
                    minWidth="0"
                    type="button"
                    text="FREE CROP"
                    onClick={this.clearAspectRatio}
                  />
                </ButtonRow>
              </ButtonCrop>
            )}
            <ReactCrop
              src={this.state.image}
              crop={this.state.crop}
              ruleOfThirds={true}
              onImageLoaded={this.onImageLoad}
              onChange={this.setImageCrop}
            />
          </ImageCropModal>
        )}
      </>
    );
  }
}

export default UploaderCrop;



// WEBPACK FOOTER //
// ./src/components/UploaderCrop.tsx